import React, { useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import Dialog from '@shared/ui/molecules/Dialog';
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { closeDialog } from '@shared/store/dialog.duck';
import { selectAccountInfo } from '@features/settings/store/payments.duck';
import { createCheckoutSession, sendBankTransferEnquiry } from '@shared/ui/organisms/stripe/stripeService';
import { BodyError, BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { SpaceBetweenRow } from '@shared/ui/atoms/StructuralLayout';
import { SwitchCounter } from '@shared/ui/molecules/SwitchCounter';
import { PlanInfoCard } from '@features/settings/pages/payments-page/PlanInfoCard';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import Colours from '@shared/ui/atoms/Colours';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { ToggleableOutlineButton } from '@shared/ui/atoms/buttons/ToggleableOutlineButton';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import {
  enableStripe,
  stripeMinUserLimit,
  stripePackSize,
  stripePremiumProductMonthlyPriceId_AUD,
  stripePremiumProductMonthlyPriceId_EUR,
  stripePremiumProductMonthlyPriceId_GBP,
  stripePremiumProductMonthlyPriceId_USD,
  stripePremiumProductYearlyPriceId_AUD,
  stripePremiumProductYearlyPriceId_EUR,
  stripePremiumProductYearlyPriceId_GBP,
  stripePremiumProductYearlyPriceId_USD,
  stripeProductMonthlyPriceId_AUD,
  stripeProductMonthlyPriceId_EUR,
  stripeProductMonthlyPriceId_GBP,
  stripeProductMonthlyPriceId_USD,
  stripeProductYearlyPriceId_AUD,
  stripeProductYearlyPriceId_EUR,
  stripeProductYearlyPriceId_GBP,
  stripeProductYearlyPriceId_USD,
  stripeUserLimit
} from '@shared/services/EnvironmentVariables';

const PACK_SIZE = stripePackSize;
const USER_LIMIT = stripeUserLimit;
const MIN_USER_LIMIT = stripeMinUserLimit;
const BASE_USERS = 10;

type Tier = 'PROFESSIONAL' | 'PREMIUM' | 'ENTERPRISE';
type Currency = 'USD' | 'GBP' | 'AUD' | 'EUR';

function getProfessionalPriceId(currency: Currency, monthly: boolean) {
  switch (currency) {
    case "AUD": return monthly ? stripeProductMonthlyPriceId_AUD : stripeProductYearlyPriceId_AUD;
    case "USD": return monthly ? stripeProductMonthlyPriceId_USD : stripeProductYearlyPriceId_USD;
    case "EUR": return monthly ? stripeProductMonthlyPriceId_EUR : stripeProductYearlyPriceId_EUR;
    default: return monthly ? stripeProductMonthlyPriceId_GBP : stripeProductYearlyPriceId_GBP;
  }
}

function getPremiumPriceId(currency: Currency, monthly: boolean) {
  switch (currency) {
    case "AUD": return monthly ? stripePremiumProductMonthlyPriceId_AUD : stripePremiumProductYearlyPriceId_AUD;
    case "USD": return monthly ? stripePremiumProductMonthlyPriceId_USD : stripePremiumProductYearlyPriceId_USD;
    case "EUR": return monthly ? stripePremiumProductMonthlyPriceId_EUR : stripePremiumProductYearlyPriceId_EUR;
    default: return monthly ? stripePremiumProductMonthlyPriceId_GBP : stripePremiumProductYearlyPriceId_GBP;
  }
}

function getPriceId(tier: Tier, currency: Currency, monthly: boolean) {
  return tier === 'PREMIUM' ? getPremiumPriceId(currency, monthly) : getProfessionalPriceId(currency, monthly);
}

function getProfessionalPrice(currency: Currency, monthly: boolean): number {
  switch (currency) {
    case "AUD": return monthly ? 18.5 : 185;
    case "USD": return monthly ? 13.3 : 133;
    case "EUR": return monthly ? 11.9 : 119;
    default: return monthly ? 10 : 100;
  }
}

// TODO: replace these PLACEHOLDER Premium per-pack prices with the real amounts from Stripe.
function getPremiumPrice(currency: Currency, monthly: boolean): number {
  switch (currency) {
    case "AUD": return monthly ? 27.5 : 275;
    case "USD": return monthly ? 19.9 : 199;
    case "EUR": return monthly ? 17.9 : 179;
    default: return monthly ? 15 : 150;
  }
}

function getPrice(tier: Tier, currency: Currency, monthly: boolean): number {
  return tier === 'PREMIUM' ? getPremiumPrice(currency, monthly) : getProfessionalPrice(currency, monthly);
}

function getCurrencySymbol(currency: 'USD' | 'GBP' | 'AUD' | 'EUR'): string {
  switch (currency) {
    case "AUD": return '$';
    case "USD": return '$';
    case "EUR": return '€';
    default: return '£';
  }
}

export default function NewPaymentDialog() {
  const dispatch = useDispatch();
  const accountInfo = useSelector(selectAccountInfo);
  const [userCount, setUserCount] = useState(PACK_SIZE);
  const [loading, setLoading] = useState(false);
  const [monthly, setMonthly] = useState(true);
  const [currency, setCurrency] = useState<Currency>("USD");
  const [tier, setTier] = useState<Tier>('PROFESSIONAL');
  const [paymentMethod, setPaymentMethod] = useState<'CARD' | 'BANK'>('CARD');
  const [error, setError] = useState('');
  const {t} = useTranslation();

  const stripeCheckout = async () => {
    if (enableStripe) {
      setLoading(true);
      try {
        const priceId = getPriceId(tier, currency, monthly);
        // The Stripe subscription quantity is the number of paid USER seats (the first BASE_USERS
        // are free), priced per seat.
        const paidUsers = userCount - BASE_USERS;
        if (!Number.isFinite(paidUsers) || paidUsers <= 0) {
          setError('There has been an error setting up your subscription, please contact hello@team-today.com');
          return;
        }
        const data = await createCheckoutSession(priceId, paidUsers);
        // Stripe.js v9 removed redirectToCheckout; redirect to the hosted Checkout URL the
        // backend returns from Session.getUrl() instead.
        if (!data.url) {
          setError('There has been an error setting up your subscription, please contact hello@team-today.com');
          return;
        }
        window.location.href = data.url;
      } catch (err: any) {
        console.error(err)
        setError('There has been an error setting up your subscription, please contact hello@team-today.com');
      } finally {
        setLoading(false);
      }
    } else {
      console.warn('Stripe disabled')
    }
  }

  const contactSalesAboutBankTransfer = () => {
    // Fire-and-forget: notify sales in the background but never let it block (or error out)
    // the user's email client opening.
    sendBankTransferEnquiry({ tier, users: userCount }).catch((err) => console.error(err));
    window.location.href = 'mailto:hello@team-today.com';
  };

  const totalPrice = (Math.ceil((userCount - BASE_USERS) / PACK_SIZE) * getPrice(tier, currency, monthly)).toFixed(2);

  // Mirrors ManageSeatsSection: paid users currently consuming seats beyond the free allowance.
  const freeUsers = accountInfo?.freeUsers ?? BASE_USERS;
  const licencesInUse = Math.ceil(Math.max(0, (accountInfo?.totalUsers ?? 0) - freeUsers));

  // Rendered as plain JSX (not a nested <QuantitySelector/> component) so it is not remounted
  // on every parent render, which would otherwise drop focus/state in the child controls.
  const quantitySelector = (
    <>
      {error && <BodyError style={{marginBottom: 16}}>{error}</BodyError>}

      <ChangePlanContainer>
        <BodyRegular weight={600} style={{ marginBottom: 12, textAlign: 'center' }}>{t('payments.your-plan')}</BodyRegular>
        <TierToggleRow>
          <ToggleableOutlineButton isActive={tier === 'PROFESSIONAL'}
                                   click={() => setTier('PROFESSIONAL')}
                                   text="payments.professional-plan" />
          <ToggleableOutlineButton isActive={tier === 'PREMIUM'}
                                   click={() => setTier('PREMIUM')}
                                   text="payments.premium-plan" />
          <ToggleableOutlineButton isActive={tier === 'ENTERPRISE'}
                                   click={() => setTier('ENTERPRISE')}
                                   text="payments.enterprise-plan" />
        </TierToggleRow>
      </ChangePlanContainer>

      <BodyRegular style={{marginTop: 32, textAlign: 'center'}} weight={600}>{t('payments.manage-total-users')}</BodyRegular>
      <SwitchCounter onChange={(val: number) => setUserCount(val)}
                     value={userCount}
                     limit={USER_LIMIT}
                     minValue={MIN_USER_LIMIT}
                     increment={PACK_SIZE}/>
      <PlanInfoCard weight={600} small style={{marginBottom: 32}}>{t('payments.licences-in-use', { used: licencesInUse, total: userCount - BASE_USERS })}</PlanInfoCard>

      <BodyRegular weight={600} style={{textAlign: 'center'}}>{t('payments.payment-method')}</BodyRegular>
      <PaymentMethodRow style={{marginBottom: 32}}>
        <ToggleableOutlineButton isActive={paymentMethod === 'CARD'}
                                 click={() => setPaymentMethod('CARD')}
                                 text="payments.card-payment" />
        <ToggleableOutlineButton isActive={paymentMethod === 'BANK'}
                                 click={() => setPaymentMethod('BANK')}
                                 text="payments.bank-transfer" />
      </PaymentMethodRow>

      {paymentMethod === 'BANK' && (
        <BankTransferInfo>
          <BodyRegular weight={600} style={{marginBottom: 12}}>
            <Trans i18nKey="payments.bank-transfer-contact" components={[<a style={{ color: Colours.blue, fontWeight: 600 }} href="mailto:hello@team-today.com">hello@team-today.com</a>]} />
          </BodyRegular>
          <ul>
            <li>{t('payments.bank-transfer-annual-only')}</li>
            <li>{t('payments.bank-transfer-currencies')}</li>
            <li>{t('payments.bank-transfer-po')}</li>
          </ul>
          <PrimaryButton text="payments.contact-us" size={ButtonSize.Large} fullWidth={true}
                         click={contactSalesAboutBankTransfer} />
        </BankTransferInfo>
      )}

      {paymentMethod === 'CARD' && (
        <>
          <BodyRegular weight={600} style={{textAlign: 'center'}}>{t('payments.how-often-you-pay')}</BodyRegular>
          <PaymentFrequencyRow style={{marginBottom: 32}}>
            <ToggleableOutlineButton isActive={monthly}
                                     click={() => setMonthly(true)}
                                     text="payments.monthly-plan" />
            <ToggleableOutlineButton isActive={!monthly}
                                     click={() => setMonthly(false)}
                                     text="payments.annual-plan" />
          </PaymentFrequencyRow>

          <BodyRegular weight={600} style={{textAlign: 'center'}}>{t('payments.currency')}</BodyRegular>
          <CurrencyRow style={{marginBottom: 32}}>
            <ToggleableOutlineButton isActive={currency === 'USD'} click={() => setCurrency('USD')} text="$ USD" />
            <ToggleableOutlineButton isActive={currency === 'GBP'} click={() => setCurrency('GBP')} text="£ GBP" />
            <ToggleableOutlineButton isActive={currency === 'EUR'} click={() => setCurrency('EUR')} text="€ EUR" />
            <ToggleableOutlineButton isActive={currency === 'AUD'} click={() => setCurrency('AUD')} text="$ AUD" />
          </CurrencyRow>

          <InfoRow>
            <BodyLarge>{t('payments.first-10-users')}</BodyLarge>
            <BodyLarge weight={600}>{t('payments.FREE')}</BodyLarge>
          </InfoRow>
          <InfoRow>
            <BodyLarge>{t('payments.total-paid-users')}</BodyLarge>
            <BodyLarge weight={600}>{userCount - BASE_USERS}</BodyLarge>
          </InfoRow>

          <InfoRow style={{marginBottom: 16}}>
            <BodyLarge weight={600}>{monthly ? t('payments.monthly-amount') : t('payments.year-amount')}</BodyLarge>
            <BodyLarge>{getCurrencySymbol(currency)}{totalPrice}</BodyLarge>
          </InfoRow>

          <PrimaryButton text="button.checkout" style={{marginBottom: 32}} size={ButtonSize.Large}
                         click={stripeCheckout} fullWidth={true} disabled={userCount <= BASE_USERS}/>
          <StripeRow>
            <img src="/assets/icons/powered-by-stripe-purple.svg" alt="Powered by Stripe" />
          </StripeRow>
        </>
      )}
    </>
  )

  return (
    <StyledDialog isOpen={true} onClose={() => dispatch(closeDialog())} showLogo={true}>
      <ContentContainer>
        {loading && <LoadingSpinner fullScreen={true}/>}
        <HeadlineMedium style={{textAlign: 'center', marginBottom: 32}}>{t('payments.manage-subscription-title')}</HeadlineMedium>
        {!loading && quantitySelector}
      </ContentContainer>
    </StyledDialog>
  )
}

const StyledDialog = styled(Dialog)<any>`
  .dialog__content {
    max-width: 680px;
  }
`;

const ContentContainer = styled.div`
  max-width: 100%;
  width: 600px;
  margin: 0 auto;
`

const InfoRow = styled(SpaceBetweenRow)`
  border-bottom: 1px solid ${Colours.mildGrey};
  padding: 12px 0;
`

const StripeRow = styled.div`
  display: flex;
  justify-content: center;
`

const PaymentFrequencyRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
`

const PaymentMethodRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
`

const BankTransferInfo = styled.div`
  margin-bottom: 32px;
  ul {
    margin: 0 0 24px 0;
    padding-left: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  li {
    font-family: proxima-nova;
    font-size: 18px;
    line-height: 16px;
    color: ${Colours.black};
  }
`

const ChangePlanContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TierToggleRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  gap: 12px;
`;

const CurrencyRow = styled.div`
  display: flex;
  justify-content: center;
  * {
    min-width: unset !important;
  }
  & > * {
    flex: 1;
    min-width: unset;
  }
`
